import React, { useState, useEffect } from 'react';
import {
  FiX, FiShield, FiAlertCircle, FiAlertTriangle, FiInfo,
  FiCheckCircle, FiRefreshCw, FiZap, FiTrendingUp, FiDollarSign,
  FiServer, FiStar,
} from 'react-icons/fi';
import { reviewArchitecture, getAiCache, saveAiCache, type ArchitectureReview, type ReviewFinding, type AiCacheHistoryEntry } from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  architectureId?: string;
}

type CategoryKey = 'security' | 'resilience' | 'performance' | 'cost' | 'bestPractices';

const CATEGORIES: { key: CategoryKey; label: string; icon: React.ReactNode }[] = [
  { key: 'security',      label: 'Sécurité',          icon: <FiShield size={15} /> },
  { key: 'resilience',    label: 'Résilience',         icon: <FiServer size={15} /> },
  { key: 'performance',   label: 'Performance',        icon: <FiZap size={15} /> },
  { key: 'cost',          label: 'FinOps',             icon: <FiDollarSign size={15} /> },
  { key: 'bestPractices', label: 'Best Practices',     icon: <FiStar size={15} /> },
];

const SEVERITY_CONFIG = {
  critical: { label: 'Critique',  bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-300',    icon: <FiAlertCircle size={14} className="text-red-600" /> },
  high:     { label: 'Élevé',     bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: <FiAlertTriangle size={14} className="text-orange-600" /> },
  medium:   { label: 'Moyen',     bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', icon: <FiInfo size={14} className="text-yellow-600" /> },
  low:      { label: 'Faible',    bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300',   icon: <FiInfo size={14} className="text-blue-500" /> },
};

const IMPACT_EFFORT_COLOR = {
  high:   'text-red-600 font-semibold',
  medium: 'text-orange-500 font-medium',
  low:    'text-green-600',
};

function ScoreRing({ score }: { score: number }) {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={88} height={88}>
        <circle cx={44} cy={44} r={r} fill="none" stroke="#e5e7eb" strokeWidth={8} />
        <circle cx={44} cy={44} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 44 44)" />
      </svg>
      <span className="absolute text-xl font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function CategoryBar({ score, label }: { score: number; label: string }) {
  const color = score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-400' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 text-gray-600 truncate">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`h-2 rounded-full ${color} transition-all duration-500`} style={{ width: `${score}%` }} />
      </div>
      <span className="w-8 text-right font-medium text-gray-700">{score}</span>
    </div>
  );
}

function FindingCard({ f }: { f: ReviewFinding }) {
  const cfg = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.low;
  return (
    <div className={`border rounded-lg p-3 ${cfg.border} ${cfg.bg}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">{cfg.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text} border ${cfg.border}`}>{cfg.label}</span>
            <span className="text-sm font-medium text-gray-800">{f.title}</span>
          </div>
          <p className="text-xs text-gray-600 mb-1">{f.description}</p>
          {f.recommendation && (
            <p className="text-xs text-gray-700"><span className="font-medium">→ </span>{f.recommendation}</p>
          )}
        </div>
      </div>
    </div>
  );
}

const ReviewModal: React.FC<Props> = ({ isOpen, onClose, nodes, edges, provider, environment, architectureName, architectureId }) => {
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [review, setReview]           = useState<ArchitectureReview | null>(null);
  const [metadata, setMetadata]       = useState<{ durationMs: number; tokensUsed: number } | null>(null);
  const [activeCategory, setActive]   = useState<CategoryKey>('security');
  const [fromCache, setFromCache]     = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();
  const [cacheContent, setCacheContent] = useState<any>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReview(null);
    setError(null);
    setActive('security');
    setFromCache(false);
    setCacheUpdatedAt(undefined);
    setCacheContent(null);
    loadOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadOrGenerate = async () => {
    if (architectureId) {
      try {
        const cached = await getAiCache(architectureId, 'review');
        setReview(cached.content.review);
        setMetadata(cached.content.metadata ? { durationMs: cached.content.metadata.durationMs, tokensUsed: cached.content.metadata.tokensUsed } : null);
        setFromCache(true);
        setCacheUpdatedAt(cached.updatedAt);
        setCacheContent(cached.content);
        return;
      } catch {
        // no cache, generate fresh
      }
    }
    generate();
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    setFromCache(false);
    setCacheUpdatedAt(undefined);
    try {
      const result = await reviewArchitecture({ nodes, edges, provider, environment, architectureName });
      setReview(result.review);
      setMetadata({ durationMs: result.metadata.durationMs, tokensUsed: result.metadata.tokensUsed });
      const payload = { review: result.review, metadata: result.metadata };
      setCacheContent(payload);
      if (architectureId) {
        saveAiCache(architectureId, 'review', payload).catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la revue');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (entry: AiCacheHistoryEntry) => {
    setReview(entry.content.review);
    setMetadata(entry.content.metadata ? { durationMs: entry.content.metadata.durationMs, tokensUsed: entry.content.metadata.tokensUsed } : null);
    setFromCache(true);
    setCacheUpdatedAt(entry.savedAt);
    setCacheContent(entry.content);
  };

  const handleExportPdf = () => {
    const w = window.open('', '_blank');
    if (!w || !review) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Revue — ${architectureName || 'Architecture'}</title>
    <style>body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111}
    h1{font-size:20px;font-weight:700;margin-bottom:4px}h2{font-size:16px;font-weight:600;margin:20px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
    h3{font-size:14px;font-weight:600;margin:12px 0 6px}.score{font-size:48px;font-weight:700;color:#6d28d9}
    .finding{border:1px solid #e5e7eb;border-radius:8px;padding:10px 14px;margin:6px 0}
    .critical{border-color:#fca5a5;background:#fef2f2}.high{border-color:#fed7aa;background:#fff7ed}
    .medium{border-color:#fde68a;background:#fffbeb}.low{border-color:#bfdbfe;background:#eff6ff}
    .badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;margin-right:6px}
    ul{margin:6px 0;padding-left:20px}li{margin:3px 0;font-size:13px}
    @media print{body{padding:16px}}</style></head><body>`);
    w.document.write(`<h1>Revue d'Architecture — ${architectureName || ''}</h1>`);
    w.document.write(`<p style="font-size:13px;color:#6b7280">Score global : <span class="score">${review.overallScore}</span>/100</p>`);
    w.document.write(`<p style="font-size:14px;color:#374151">${review.summary}</p>`);
    if (review.strengths?.length) {
      w.document.write('<h2>Points forts</h2><ul>');
      review.strengths.forEach(s => w.document.write(`<li>${s}</li>`));
      w.document.write('</ul>');
    }
    Object.entries(review.categories).forEach(([key, cat]: [string, any]) => {
      const labels: Record<string, string> = { security: 'Sécurité', resilience: 'Résilience', performance: 'Performance', cost: 'FinOps', bestPractices: 'Best Practices' };
      w.document.write(`<h2>${labels[key] || key} — ${cat.score}/100</h2>`);
      cat.findings?.forEach((f: any) => {
        w.document.write(`<div class="finding ${f.severity}"><strong>${f.title}</strong><p style="font-size:13px;margin:4px 0">${f.description}</p>${f.recommendation ? `<p style="font-size:12px;color:#4b5563">→ ${f.recommendation}</p>` : ''}</div>`);
      });
    });
    w.document.write('</body></html>');
    w.document.close();
    w.print();
  };

  if (!isOpen) return null;

  const currentCategory = review ? review.categories[activeCategory] : null;
  const criticalCount = review
    ? Object.values(review.categories).flatMap(c => c.findings).filter(f => f.severity === 'critical').length
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <FiShield size={18} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Revue d'Architecture IA</h2>
              {architectureName && <p className="text-xs text-gray-500">{architectureName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {review && (
              <button onClick={generate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <FiRefreshCw size={12} /> Regénérer
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Analyse en cours…</p>
            </div>
          )}

          {error && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <FiAlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-red-700">Erreur lors de la revue</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <button onClick={generate} className="mt-2 text-xs text-red-700 underline">Réessayer</button>
              </div>
            </div>
          )}

          {review && (
            <div className="p-6 space-y-6">

              {/* Score global + résumé */}
              <div className="flex items-start gap-6 p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="shrink-0">
                  <ScoreRing score={review.overallScore} />
                  <p className="text-center text-xs text-gray-500 mt-1">Score global</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 mb-4 leading-relaxed">{review.summary}</p>
                  <div className="space-y-2">
                    {CATEGORIES.map(c => (
                      <CategoryBar key={c.key} label={c.label} score={review.categories[c.key]?.score ?? 0} />
                    ))}
                  </div>
                </div>
                {criticalCount > 0 && (
                  <div className="shrink-0 flex flex-col items-center p-3 bg-red-50 border border-red-200 rounded-xl">
                    <span className="text-2xl font-bold text-red-600">{criticalCount}</span>
                    <span className="text-xs text-red-500 text-center">problème{criticalCount > 1 ? 's' : ''}<br/>critique{criticalCount > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              {/* Points forts & Quick wins */}
              <div className="grid grid-cols-2 gap-4">
                {review.strengths?.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
                    <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1.5">
                      <FiCheckCircle size={13} /> Points forts
                    </h4>
                    <ul className="space-y-1">
                      {review.strengths.map((s, i) => (
                        <li key={i} className="text-xs text-green-800 flex items-start gap-1.5">
                          <span className="text-green-500 mt-0.5">✓</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {review.quickWins?.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                    <h4 className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1.5">
                      <FiTrendingUp size={13} /> Quick wins
                    </h4>
                    <ul className="space-y-1">
                      {review.quickWins.map((w, i) => (
                        <li key={i} className="text-xs text-blue-800 flex items-start gap-1.5">
                          <span className="text-blue-500 mt-0.5">→</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Top recommandations */}
              {review.topRecommendations?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Top recommandations</h3>
                  <div className="space-y-2">
                    {review.topRecommendations.map((r, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50">
                        <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0">{r.priority}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-0.5">
                            <span className="text-sm font-medium text-gray-800">{r.title}</span>
                            <span className={`text-xs ${IMPACT_EFFORT_COLOR[r.impact]}`}>Impact {r.impact}</span>
                            <span className="text-xs text-gray-400">· Effort {r.effort}</span>
                          </div>
                          <p className="text-xs text-gray-600">{r.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Détail par catégorie */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Détail par catégorie</h3>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {CATEGORIES.map(c => {
                    const cat = review.categories[c.key];
                    const hasCritical = cat?.findings?.some(f => f.severity === 'critical');
                    return (
                      <button
                        key={c.key}
                        onClick={() => setActive(c.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          activeCategory === c.key
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        {c.icon} {c.label}
                        {hasCritical && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                        <span className={`ml-0.5 ${activeCategory === c.key ? 'text-purple-200' : 'text-gray-400'}`}>
                          {cat?.score ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {currentCategory && (
                  currentCategory.findings.length === 0
                    ? (
                      <div className="flex items-center gap-2 p-4 bg-green-50 rounded-xl border border-green-100 text-sm text-green-700">
                        <FiCheckCircle size={16} /> Aucun problème détecté dans cette catégorie.
                      </div>
                    )
                    : (
                      <div className="space-y-2">
                        {currentCategory.findings.map((f, i) => <FindingCard key={i} f={f} />)}
                      </div>
                    )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(metadata || review) && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 shrink-0">
            <AiCacheActions
              architectureId={architectureId}
              cacheType="review"
              content={cacheContent}
              fromCache={fromCache}
              updatedAt={cacheUpdatedAt}
              onRestore={handleRestore}
              onExportPdf={handleExportPdf}
            />
            <div className="flex items-center gap-4">
              {metadata && <><span>{metadata.tokensUsed.toLocaleString()} tokens</span><span>{(metadata.durationMs / 1000).toFixed(1)}s</span></>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
